import {
  Button,
  Checkbox,
  Container,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Icon,
  MenuItem,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { BsPaperclip } from 'react-icons/all';
import download from 'downloadjs';
import { GeneralInformation } from '~/pages/Portal/Sinisters/GeneralInformation';
import { useSinister } from '~/hooks/useSinisters/useSinisters';
import { SinistersDocuments } from '~/types/sinisters';
import { getSinisterPdf } from '~/services/Sinisters';
import { formatCurrency } from '~/utils/formatCurrency';
import apiSinister from '~/utils/apiSinister';
import { queryClient } from '~/utils/react-query';

export const SinistersDownloadDebitNote: React.FC<{
  sinisterId: number;
  link?: boolean;
}> = ({ sinisterId, link = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);

  const [descricao, setDescricao] = useState<string>('');
  const [valor, setValor] = useState<string>('');
  const { data, isLoading, refetch } = useSinister(sinisterId);
  const [isDownloading, setIsDownloading] = useState(false);
  const [visualizada, setVisualizada] = useState<boolean>(false);

  useEffect(() => {
    if (link) handleOpen();
  }, []);

  const handleOpen = () => {
    setDescricao(data!.nota_de_debito_referente);
    const v = formatCurrency(Number(data!.nota_de_debito_valor));
    setValor(v);
    onOpen();
    setVisualizada(data!.nota_de_debito_visualizada);
  };
  const handleVisualized = () => {
    setFetching(true);
    apiSinister
      .post(`/comprovar-pagamento/${data?.id}/gerar-historico-visualizacao/`, {
        user_id: sinisterId,
        visualizado: visualizada,
      })
      .then(() => {
        queryClient.invalidateQueries(['sinister', sinisterId]);
        handleClose();
      })
      .finally(() => {
        setFetching(false);
        toast({
          title: `Confirmação de visualização da nota de débito enviada`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setDescricao('');
    clearErrors();
  };

  function handleDownload() {
    setIsDownloading(true);
    const url = SinistersDocuments[8];
    getSinisterPdf(data!.id.toString(), url)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            `${url}_${data?.numero_sinistro}`,
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        console.log(err);
        toast({
          title: 'Erro ao gerar nota de débito',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }

  return (
    <>
      <MenuItem onClick={handleOpen}>Baixar nota de débito</MenuItem>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader>Baixar nota de débito</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={600} w="100%">
              <Stack gridGap={4}>
                <GeneralInformation sinister={data!} />
                <FormControl>
                  <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                    Referente:
                  </FormLabel>
                  <Text>{descricao}</Text>
                </FormControl>
                <VStack>
                  <Container
                    maxW="container.lg"
                    bg="gray.100"
                    padding="1rem 1rem 1rem 1rem"
                    borderRadius="5px"
                    borderColor="gray.300"
                    borderWidth="1px"
                  >
                    <Grid
                      templateRows="repeat(2, 1fr)"
                      templateColumns="repeat(4, 1fr)"
                      fontSize="14px"
                    >
                      <GridItem colSpan={2}>
                        <Text fontWeight="bold">Valor:</Text>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <Text fontWeight="bold">Nota de débito:</Text>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <Text>{valor}</Text>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <Text
                          _hover={{
                            color: 'gray.900',
                            textDecoration: 'underline',
                          }}
                          cursor="pointer"
                          onClick={() => handleDownload()}
                        >
                          Visualizar <Icon as={BsPaperclip} fontSize="xl" />
                        </Text>
                      </GridItem>
                    </Grid>
                  </Container>
                </VStack>
                <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                  Confirmar visualização da nota de débito?
                  <Checkbox
                    isChecked={visualizada}
                    marginLeft="10px"
                    onChange={e => {
                      setVisualizada(e.target.checked);
                    }}
                    isDisabled={data?.nota_de_debito_visualizada}
                    iconColor="black"
                  />
                </FormLabel>
              </Stack>
            </Stack>
            {!data?.nota_de_debito_visualizada && visualizada ? (
              <Button
                type="submit"
                onClick={handleVisualized}
                color="white"
                background="gray.900"
                _hover={{
                  bgColor: 'black',
                }}
                style={{ marginTop: '20px' }}
                isDisabled={isFetching}
              >
                Confirmar
                {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
              </Button>
            ) : (
              <Button
                type="submit"
                onClick={handleClose}
                color="white"
                background="gray.900"
                _hover={{
                  bgColor: 'black',
                }}
                style={{ marginTop: '20px' }}
                isDisabled={isFetching}
              >
                Fechar
              </Button>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
